import { useState } from 'react';
import { Stack, Group, Text, Button, Alert, TextInput, Collapse } from '@mantine/core';
import { OpcionesGrandes } from './OpcionesGrandes';
import { TecladoNumerico } from './TecladoNumerico';
import { UI } from '../../theme/kioskTheme';

const MITADES = [{ value: 'A', label: 'Mitad A' }, { value: 'B', label: 'Mitad B' }];

/**
 * Salida de emergencia para una entrega que el sistema rechazó.
 *
 * La validación se queda: el aviso rojo aparece igual y el operador tiene que
 * leerlo. Lo que se agrega es la posibilidad de seguir a pesar de él, porque
 * en un evento un "no" sin salida es peor que un dato imperfecto — alguien que
 * se va en veinte minutos, una bolsa que se mezcló, la entrega que arranca
 * diez minutos tarde.
 *
 * Dos límites a propósito:
 *
 *   - Lo autoriza el PIN de DIRECCIÓN, no el del stand. Quien opera la tablet
 *     no puede saltear controles solo; tiene que venir alguien del equipo.
 *     El PIN viaja en esa llamada y no se guarda: la tablet no queda con
 *     permiso de dirección.
 *   - No todo es forzable. El backend marca `forzable: false` cuando el dato
 *     que saldría no se puede representar — un tercer dueño de un número, por
 *     ejemplo. En esos casos este panel no aparece.
 */
export function ForzarEntrega({ error, enviando, onForzar }) {
  const [abierto, setAbierto] = useState(false);
  const [pin, setPin] = useState('');
  const [motivo, setMotivo] = useState('');
  const [mitad, setMitad] = useState(null);

  if (!error) return null;

  const puede = Boolean(error.forzable);
  const necesitaMitad = Boolean(error.pideMitad);
  const listo = pin.length >= 4 && motivo.trim().length >= 3 && (!necesitaMitad || mitad);

  return (
    <Stack gap="xs">
      <Alert color="red" radius="md" styles={{ message: { fontSize: 19, fontWeight: 600 } }}>
        {error.message || 'No pudimos entregar la caravana.'}
      </Alert>

      {puede && !abierto && (
        <Button variant="subtle" size="sm" c="dimmed" onClick={() => setAbierto(true)}>
          Forzar la entrega (necesita PIN de dirección)
        </Button>
      )}

      <Collapse in={puede && abierto}>
        <Stack gap="sm" p="md"
               style={{ border: `2px solid ${UI.borde}`, borderRadius: 10 }}>
          <Text fw={700} c="forvet.8">Entrega forzada</Text>
          <Text fz="var(--fs-small)" c="forvet.6">
            Queda registrado qué control se salteó y por qué. El juego sigue
            funcionando igual: el par se cierra, las pistas se calculan y los
            avisos salen como siempre.
          </Text>

          {necesitaMitad && (
            <Stack gap={4}>
              <Text fw={600} fz="var(--fs-small)">¿Qué mitad estás entregando?</Text>
              <OpcionesGrandes options={MITADES} value={mitad} onChange={setMitad} />
            </Stack>
          )}

          <TextInput
            label="Motivo"
            placeholder="Se va antes del cierre, la bolsa estaba mezclada…"
            value={motivo}
            onChange={e => setMotivo(e.currentTarget.value)}
          />

          <Stack gap={4}>
            <Text fw={600} fz="var(--fs-small)">PIN de dirección</Text>
            <TecladoNumerico value={pin} onChange={setPin} maxLen={6} />
          </Stack>

          <Group grow>
            <Button variant="outline" onClick={() => { setAbierto(false); setPin(''); }}>
              CANCELAR
            </Button>
            <Button color="red" loading={enviando} disabled={!listo}
                    onClick={() => onForzar({ pinDireccion: pin, motivo: motivo.trim(), mitad })}>
              FORZAR
            </Button>
          </Group>
        </Stack>
      </Collapse>
    </Stack>
  );
}
